import type {
  DispatchBoard,
  DispatchFacility,
  DispatchJob,
  DispatchNurse,
} from "@vellum/vellum-api";

/** How often the board refetches while the page is visible. */
export const POLL_INTERVAL_MS = 10_000;

/**
 * A facility pin's state, loudest first: any open job late, any open job
 * nobody is assigned to, open jobs all assigned, or everything visited.
 */
export type FacilityMark = "late" | "unassigned" | "open" | "done";

export type BoardIndex = {
  facilities: Map<string, DispatchFacility>;
  nurses: Map<string, DispatchNurse>;
  jobsByFacility: Map<string, DispatchJob[]>;
};

export function indexBoard(board: DispatchBoard | null): BoardIndex {
  const jobsByFacility = new Map<string, DispatchJob[]>();
  for (const job of board?.jobs ?? []) {
    const list = jobsByFacility.get(job.facility_id) ?? [];
    list.push(job);
    jobsByFacility.set(job.facility_id, list);
  }
  return {
    facilities: new Map(
      (board?.facilities ?? []).map((facility) => [facility.id, facility]),
    ),
    nurses: new Map((board?.nurses ?? []).map((nurse) => [nurse.id, nurse])),
    jobsByFacility,
  };
}

export function isDone(job: DispatchJob): boolean {
  return Boolean(job.check_out_at);
}

export function isOnSite(job: DispatchJob): boolean {
  return Boolean(job.check_in_at) && !job.check_out_at;
}

export function facilityMark(jobs: DispatchJob[]): FacilityMark {
  const open = jobs.filter((job) => !isDone(job));
  if (open.some((job) => job.late)) return "late";
  if (open.some((job) => !job.nurse_id)) return "unassigned";
  return open.length ? "open" : "done";
}

export type JobGroups = {
  unassigned: DispatchJob[];
  /** Assigned and not finished: on site now or still to visit, by when due. */
  upcoming: DispatchJob[];
  done: DispatchJob[];
};

/** Splits the board's jobs, keeping the server's order (by when each is due). */
export function groupJobs(jobs: DispatchJob[]): JobGroups {
  const groups: JobGroups = { unassigned: [], upcoming: [], done: [] };
  for (const job of jobs) {
    if (isDone(job)) groups.done.push(job);
    else if (!job.nurse_id) groups.unassigned.push(job);
    else groups.upcoming.push(job);
  }
  return groups;
}

export function coordinatesOf(
  facility: DispatchFacility | undefined,
): [number, number] | null {
  if (facility?.lat == null || facility.long == null) return null;
  return [facility.long, facility.lat];
}

/** Where the board places a nurse: the facility they're at or last left, else home. */
export function nursePosition(
  nurse: DispatchNurse,
  facilities: Map<string, DispatchFacility>,
): [number, number] | null {
  if (nurse.facility_id) {
    return coordinatesOf(facilities.get(nurse.facility_id));
  }
  if (nurse.home_lat == null || nurse.home_long == null) return null;
  return [nurse.home_long, nurse.home_lat];
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

export const JOB_TYPE_LABELS: Record<string, string> = {
  Infusion: "Infusion",
  "Vascular Access": "Vascular access",
};

const VIEWER_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * A time in the facility's own zone, "10:15 AM", naming the zone when it
 * isn't the viewer's. `withDate` adds the day, for jobs carried over.
 */
export function formatClock(
  iso: string,
  timeZone: string,
  withDate = false,
): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    ...(withDate ? { month: "short", day: "numeric" } : {}),
    ...(timeZone === VIEWER_ZONE ? {} : { timeZoneName: "short" }),
  }).format(new Date(iso));
}

/** "25 min", "1 h 5 min", or whole days once it's a day or more. */
export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const days = Math.floor(minutes / (24 * 60));
  if (days > 0) return days === 1 ? "1 day" : `${days} days`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

/** Seconds from `earlier` to `later`. */
export function secondsBetween(later: string, earlier: string): number {
  return (new Date(later).getTime() - new Date(earlier).getTime()) / 1000;
}
